import type {
  AssetType,
  BuilderContext,
  RuleResult,
  Severity,
  ValidationRule,
} from "./types";

/**
 * Rule registry. Rules register themselves at module load; the builder asks for
 * the subset that applies to the asset it is validating.
 *
 * Kept deliberately small: adding a rule should never mean touching the runner,
 * the popover, or the panel.
 */
const registry = new Map<string, ValidationRule>();

/** Registers a rule. Throws on duplicate ids — they are Pendo keys and must be unique. */
export const registerRule = (rule: ValidationRule): ValidationRule => {
  if (registry.has(rule.id)) {
    throw new Error(`Duplicate validation rule id: ${rule.id}`);
  }
  registry.set(rule.id, rule);
  return rule;
};

export const registerRules = (rules: ValidationRule[]): void => {
  rules.forEach(registerRule);
};

/** All registered rules, in registration order. */
export const allRules = (): ValidationRule[] => [...registry.values()];

export const getRule = (id: string): ValidationRule | undefined =>
  registry.get(id);

/** Rules that apply to an asset type at a given severity. */
export const rulesFor = (
  assetType: AssetType,
  severity?: Severity
): ValidationRule[] =>
  allRules().filter(
    (rule) =>
      rule.assetTypes.includes(assetType) &&
      (severity === undefined || rule.severity === severity)
  );

/**
 * Runs the matching rules against a context snapshot.
 *
 * A rule that throws is skipped rather than failing the whole run: one broken
 * rule must never be able to block every save in the builder. The failure is
 * logged so it surfaces in Sentry instead of disappearing.
 */
export const runRules = (
  ctx: BuilderContext,
  severity: Severity
): RuleResult[] => {
  const results: RuleResult[] = [];
  rulesFor(ctx.assetType, severity).forEach((rule) => {
    if (rule.gate && !rule.gate(ctx)) return;
    try {
      results.push(...rule.evaluate(ctx));
    } catch (error) {
      console.error(`Validation rule "${rule.id}" failed to evaluate`, error);
    }
  });
  return results;
};

/** Test-only. Clears the registry so suites do not leak rules into each other. */
export const __resetRegistry = (): void => {
  registry.clear();
};
